import React, { useState, useEffect } from "react";
import { TextSwitch } from "../../../../components/TextSwitch/TextSwitch";
import { ArchivingTypeEnum } from "../../../../constants/AppConstants";
import {
  CM_SHEET_ARCHIVING_LABEL,
  CM_SHEET_ARCHIVING_MESSEGE_1,
  EMPTY_ARCHIVE_ALL_SHEETS_MESSEGE,
  FINAL_CONSIDERATIONS_PENDING_LABEL,
  NO_CM_SHEETS_TO_ARCHIVE_MESSEGE,
  NOTHING_TO_ARCHIVE_LABEL,
  PROCESSING_ARCHIVING_MESSAGE,
  READY_SEND_SHEET_LABEL,
  RESOURCES_LABEL,
} from "../../../../constants/Dictionary";
import {
  StyledAlertMessage,
  StyledManageArchiving,
} from "./ManageArchiving.styled";
import { PendingFinalFeedback } from "./PendingFinalFeedback/PendingFinalFeedback";
import { ReadySendSheet } from "./ReadySendSheet/ReadySendSheet";
import emptyArchiveAllSheetsImage from "../../../../images/archiveAllSheets.png";
import noCmSheetsToArchive from "../../../../images/noCmSheetsToArchive.png";
import { EmptyState } from "../../../../components/EmptyState/EmptyState";
import { Loader } from "../../../../components/core/Loader/Loader";
import { ReactComponent as DatabaseIcon } from "../../../../icons/database.svg";
import { Button } from "../../../../components/core/Button/Button";
import { PopupConfirm } from "../../../../components/PopupConfirm/PopupConfirm";
import { StyledLoadingContainer } from "../../Management.styled";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../../../hooks/store.hook";
import { setTabsManagementArchiving } from "../../../../stores/actions/management.actions";
import { ReactComponent as WarningIcon } from "../../../../icons/warning.svg";
import { IconManager } from "../../../../components/core/IconManager/IconManager";
import { BodyTyp } from "../../../../components/Typography/Typography";
import { theme } from "../../../../constants/StyledComponents";
import { IconSizeEnum, FontWeightEnum } from "../../../../types/AppTypes";
import { MessageBox } from "../../../../components/MessageBox/MessageBox";
import {
  useArchiveAllCmSheets,
  useArchivingStats,
} from "../../../../hooks/manageArchiving.hook";

export const MenagementArchiving = () => {
  const dispatch = useDispatch();
  const [openPopup, setOpenPopup] = useState(false);

  const { tabsManagementArchiving } = useAppSelector(
    (state) => state.management,
  );

  const ArchivingTypeOptions = [
    {
      value: ArchivingTypeEnum.FINAL_FEEDBACK_PENDING,
      label: FINAL_CONSIDERATIONS_PENDING_LABEL,
    },
    {
      value: ArchivingTypeEnum.READY_SEND_SHEET,
      label: READY_SEND_SHEET_LABEL,
    },
  ];

  const { data: statsData, isFetching: fetchingStats } = useArchivingStats();
  const { mutateAsync: archiveAllCmSheets, isPending: isArchivingAllCmSheets } =
    useArchiveAllCmSheets();

  const handleConfirmPopup = async () => {
    try {
      await archiveAllCmSheets();
      setOpenPopup(false);
    } catch (error) {
      console.error(`error adding new external collaborator ${error}`);
      setOpenPopup(false);
    }
  };

  const handleCancelPopup = () => {
    setOpenPopup(false);
  };

  useEffect(() => {
    if (ArchivingTypeOptions.length > 0 && !tabsManagementArchiving) {
      dispatch(setTabsManagementArchiving(ArchivingTypeOptions[0]));
    }
  }, [ArchivingTypeOptions, tabsManagementArchiving, dispatch]);

  const allCmSheetsAreArchived =
    !fetchingStats &&
    statsData &&
    statsData.pendingFinalFeedback === 0 &&
    statsData.readyToSend === 0 &&
    statsData.finalFeedbackRevisionSent === 0;

  const getPopupText = () => {
    return `${CM_SHEET_ARCHIVING_MESSEGE_1} ${statsData?.finalFeedbackRevisionSent} ${RESOURCES_LABEL}?`;
  };

  const checkToSeeTable =
    !fetchingStats &&
    statsData &&
    ((statsData.pendingFinalFeedback && statsData.pendingFinalFeedback > 0) ||
      (statsData.readyToSend && statsData.readyToSend > 0));

  const checkToSeeBtnToArchiveAll =
    !fetchingStats &&
    statsData &&
    statsData.pendingFinalFeedback === 0 &&
    statsData.readyToSend === 0 &&
    statsData.finalFeedbackRevisionSent &&
    statsData.finalFeedbackRevisionSent > 0;

  const nothingToArchive =
    !fetchingStats &&
    statsData &&
    statsData.pendingFinalFeedback === null &&
    statsData.readyToSend === null &&
    statsData.finalFeedbackRevisionSent === null;

  return (
    <StyledManageArchiving>
      {fetchingStats ? (
        <StyledLoadingContainer>
          <Loader loading={true} />
        </StyledLoadingContainer>
      ) : null}
      {checkToSeeTable ? (
        <>
          <div className="text-switch-container">
            <TextSwitch
              selectedOption={tabsManagementArchiving}
              setSelectedOption={(option) =>
                dispatch(setTabsManagementArchiving(option))
              }
              options={ArchivingTypeOptions}
            />
          </div>
          {tabsManagementArchiving?.value ===
            ArchivingTypeEnum.FINAL_FEEDBACK_PENDING && (
            <PendingFinalFeedback />
          )}
          {tabsManagementArchiving?.value ===
            ArchivingTypeEnum.READY_SEND_SHEET && (
            <ReadySendSheet stats={statsData} />
          )}
        </>
      ) : null}
      {checkToSeeBtnToArchiveAll ? (
        <div className="archiving-all-container">
          <EmptyState
            label={EMPTY_ARCHIVE_ALL_SHEETS_MESSEGE}
            imageSrc={emptyArchiveAllSheetsImage}
          />
          <Button
            icon={<DatabaseIcon />}
            label={CM_SHEET_ARCHIVING_LABEL}
            onClick={() => setOpenPopup(true)}
          />
        </div>
      ) : null}
      {allCmSheetsAreArchived ? (
        <div className="archiving-all-container">
          <EmptyState
            label={NO_CM_SHEETS_TO_ARCHIVE_MESSEGE}
            imageSrc={noCmSheetsToArchive}
          />
        </div>
      ) : null}
      {nothingToArchive ? (
        <div className="nothing-to-archive-container">
          <MessageBox message={NOTHING_TO_ARCHIVE_LABEL} />
        </div>
      ) : null}
      {openPopup && (
        <PopupConfirm
          isOpen={openPopup}
          label={CM_SHEET_ARCHIVING_LABEL}
          popupText={getPopupText()}
          onConfirm={handleConfirmPopup}
          onCancel={handleCancelPopup}
          isLoading={isArchivingAllCmSheets}
          loadingMessage={
            <StyledAlertMessage>
              <IconManager size={IconSizeEnum.SMALL} color={theme.colors.red_1}>
                <WarningIcon />
              </IconManager>
              <BodyTyp
                color={theme.colors.red_1}
                fontWeight={FontWeightEnum.SEMIBOLD}
              >
                {PROCESSING_ARCHIVING_MESSAGE}
              </BodyTyp>
            </StyledAlertMessage>
          }
        />
      )}
    </StyledManageArchiving>
  );
};
